import { SchnellbuehneSchema } from '../../shared/schnellbuehnen';
import './Schnellchat.css';
import { schnellchatDokument } from './schnellchat-html';
import { httpBoundary } from '@werkingflow/trust-boundary';
import { createPortal } from 'react-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { z } from 'zod';

const Chat = z.object({ id: z.string().uuid(), parent: z.string().uuid(), laufend:z.object({modus:z.enum(['frage','schnellbuehne']),text:z.string()}).passthrough().optional(), fehler:z.string().optional(), schnellbuehne: SchnellbuehneSchema.optional(), geschlossen: z.boolean(), kontextAm: z.string().optional(),
  nachrichten: z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string(), am: z.string(), html: z.string().optional() })), zusammenfassung: z.string().optional() });
type Verlauf = z.infer<typeof Chat>;
const basis = '/api/guide/coach/schnellchat';
async function api(path: string, body?: object, haus?: string): Promise<Verlauf> {
  return httpBoundary({ name: 'cui.schnellchat', url: basis + (haus ? '/haus/' + haus : '') + path, schema: Chat,
    method: body ? 'POST' : 'GET', body, timeoutMs: 100_000,
    onStatus: (status, responseText) => {
      if (status < 400) return undefined;
      let data: unknown;
      try { data = JSON.parse(responseText); } catch { return { throw: new Error('Schnellchat nicht erreichbar.') }; }
      const error = z.object({ error: z.string() }).safeParse(data);
      return { throw: new Error(error.success ? error.data.error : 'Schnellchat nicht erreichbar.') };
    },
  });
}
/** A separate chat; no parent send/stop/resume action on open or question. */
export function Schnellchat({ parent, buehneId, haus, onOffen }: { parent: string; buehneId?: string; haus?: 'dev' | 'partner'; onOffen: (offen: boolean) => void }) {
  const [offen, setOffen] = useState(false);
  const [gross, setGross] = useState(false);
  const [breite, setBreite] = useState(440);
  const [arbeitsbereich, setArbeitsbereich] = useState<HTMLElement | null>(null);
  const [chat, setChat] = useState<Verlauf | null>(null);
  const [text, setText] = useState('');
  const [modus, setModus] = useState<'frage' | 'schnellbuehne'>('frage');
  function waehleModus(wert: 'frage' | 'schnellbuehne') {
    setModus(wert);
    if (chat) { try { localStorage.setItem(`cui-schnellchat-modus:${chat.id}`, wert); } catch { /* Selection remains active in this window. */ } }
  }
  const [darstellung, setDarstellung] = useState<number | null>(null);
  const html = darstellung === null ? chat?.schnellbuehne?.html ?? [...(chat?.nachrichten ?? [])].reverse().find(n => n.html)?.html : chat?.nachrichten[darstellung]?.html;
  const dokument = useMemo(() => html ? schnellchatDokument(html) : '', [html]);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const [hinweis, setHinweis] = useState('');
  const [zusammenfassung, setZusammenfassung] = useState('');
  const [ohneOption, setOhneOption] = useState(false);
  useEffect(() => { onOffen(offen); return () => onOffen(false); }, [offen, onOffen]);
  const key = `cui-schnellchat:${parent}`;
  const ende = useRef<HTMLDivElement>(null);
  const eingabe = useRef<HTMLTextAreaElement>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  function einklappen() { try { localStorage.removeItem(key + ':offen'); } catch { /* UI preference only. */ } setOffen(false); knopf.current?.focus(); }
  const gesperrt = useRef(false);
  useEffect(() => { ende.current?.scrollIntoView({ block: 'nearest' }); }, [chat]);
  useEffect(() => { if (offen && !busy) eingabe.current?.focus(); }, [offen, busy]);
  async function oeffnen() {
    if (offen) { einklappen(); return; }
    setGross(false);
    await oeffnenNachFehler();
  }
  async function oeffnenNachFehler() {
    setArbeitsbereich(knopf.current?.closest<HTMLElement>('[data-ai-id="buehnen-fenster"]') ?? Array.from(document.querySelectorAll<HTMLElement>('[data-ai-id="buehnen-fenster"]')).find(el => el.getBoundingClientRect().width > 0) ?? knopf.current?.closest<HTMLElement>('.flexlayout__tab') ?? null);
    setOffen(true);
    try { localStorage.setItem(key + ':offen', '1'); } catch { /* UI preference only. */ }
    if (gesperrt.current) return;
    setFehler(''); setHinweis('');

    gesperrt.current = true; setBusy(true);
    // fallback-ok: Error remains visible in role=alert; transcript and draft preserved, no success fallback.
    try {
      let id: string | null = null;
      try { id = localStorage.getItem(key); } catch { /* Server still persists the chat. */ }
      let c = id ? await api('/' + id, undefined, haus) : null;
      if (!c || c.geschlossen) c = await api('', { parent, buehneId }, haus);
      if (c.parent !== parent) throw new Error('Dieser Schnellchat gehört zu einer anderen Unterhaltung.');
      let gewaehlt = c.laufend?.modus ?? ([...c.nachrichten].reverse().find(n => n.role === 'assistant')?.html ? 'schnellbuehne' : 'frage');
      try { const gespeichert = localStorage.getItem(`cui-schnellchat-modus:${c.id}`); if (gespeichert === 'frage' || gespeichert === 'schnellbuehne') gewaehlt = gespeichert; } catch { /* Use the current conversation to choose its initial mode. */ }
      setModus(gewaehlt);
      setChat(c); if (c.fehler) setFehler(c.fehler); try { localStorage.setItem(key, c.id); } catch { /* In-memory chat remains usable. */ }
    } catch (e) { setFehler(e instanceof Error ? e.message : 'Öffnen fehlgeschlagen.'); }
    finally { gesperrt.current = false; setBusy(false); }
  }
  useEffect(() => {
    try { if (localStorage.getItem(key + ':offen') === '1') void oeffnenNachFehler(); } catch { /* UI preference only. */ }
  }, [key]);
  async function senden() {
    if (gesperrt.current || !chat || !text.trim()) return;
    gesperrt.current = true; setBusy(true); setFehler('');
    // fallback-ok: Error remains visible in role=alert; transcript and draft preserved, no success fallback.
    try {
      const c = await api(`/${chat.id}/fragen`, { text: text.trim(), modus }, haus);
      setChat(c); setText('');
      setDarstellung(null);

    }
    catch (e) { setFehler(e instanceof Error ? e.message : 'Antwort fehlgeschlagen.'); }
    finally { gesperrt.current = false; setBusy(false); }
  }
  useEffect(() => {
    if (!chat?.laufend) return;
    let aktiv = true; let timer: ReturnType<typeof setTimeout>;
    const id = chat.id;
    gesperrt.current = true; setBusy(true);
    const lesen = async () => {
      // fallback-ok: Poll errors remain visible and retry; the durable server turn is not discarded.
      try {
        const c = await api('/'+id, undefined, haus);
        if (!aktiv) return;
        setChat(c);
        if (c.laufend) { timer = setTimeout(lesen, 900); return; }
        gesperrt.current = false; setBusy(false);
        if (c.fehler) { setFehler(c.fehler); return; }
        setDarstellung(null);
      } catch (e) { if (aktiv) { setFehler(e instanceof Error ? e.message : 'Verlauf nicht erreichbar.'); timer = setTimeout(lesen, 2000); } }
    };
    timer = setTimeout(lesen, 900);
    return () => { aktiv = false; clearTimeout(timer); };
  }, [chat?.id, !!chat?.laufend, haus]);
  async function schliessen(ohneZusammenfassung = false) {
    if (gesperrt.current) return;
    if (!chat) { setOffen(false); return; }
    gesperrt.current = true; setBusy(true); setFehler('');
    // fallback-ok: Error remains visible in role=alert; transcript and draft preserved, no success fallback.
    try {
      setOhneOption(false);
      const c = await api(`/${chat.id}/schliessen`, ohneZusammenfassung ? { ohneZusammenfassung: true } : {}, haus);
      setChat(c); setText(''); setDarstellung(null); einklappen();
      try { localStorage.removeItem(key); } catch { /* Server records the closed state. */ }
      setHinweis(c.nachrichten.length ? 'Für den nächsten Zug des Beraters gespeichert.' : 'Schnellchat geschlossen.');
      setZusammenfassung(c.zusammenfassung ?? '');
    } catch (e) { setFehler(e instanceof Error ? e.message : 'Übergabe fehlgeschlagen.'); setOhneOption(true); }
    finally { gesperrt.current = false; setBusy(false); }
  }
  const fenster = <aside hidden={!offen} className={`schnellchat-drawer${gross ? ' is-expanded' : ''}${modus === 'frage' ? ' is-chat' : ' is-stage'}`} style={gross ? undefined : { width: modus === 'frage' ? breite : Math.max(580, breite) }} role="dialog" aria-modal="false" aria-label="Schnellchat mit Haiku" onClick={e => e.stopPropagation()}
      onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); if (gross) setGross(false); else einklappen(); } }}>
      <div className="schnellchat-resize" role="separator" aria-label="Schnellchat verbreitern" aria-orientation="vertical" tabIndex={0}
        onKeyDown={e => { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); setBreite(w => Math.max(340, w + (e.key === 'ArrowLeft' ? 40 : -40))); } }}
        onPointerDown={e => { e.currentTarget.setPointerCapture(e.pointerId); }}
        onPointerMove={e => { if (e.currentTarget.hasPointerCapture(e.pointerId)) { setGross(false); const bounds = e.currentTarget.parentElement?.parentElement?.getBoundingClientRect(); if (bounds) setBreite(Math.max(340, Math.min(bounds.width - 20, bounds.right - e.clientX - 10))); } }} />
      <header className="schnellchat-heading">
        <div className="schnellchat-mark" aria-hidden="true">✦</div>
        <div className="schnellchat-heading-text"><strong>{modus === 'frage' ? 'Zwischenfrage' : 'Schnellbühne'}</strong><span>Abzweig deines Beraters · kennt das ganze Gespräch</span></div>
        <button type="button" className="schnellchat-minimize" data-ai-id="schnellchat-vergroessern" aria-label={gross ? "Verkleinern" : "Arbeitsbereich ausfüllen"} title={gross ? "Verkleinern" : "Arbeitsbereich ausfüllen"} onClick={() => setGross(v => !v)}>{gross ? "↙" : "⛶"}</button>
        <button type="button" className="schnellchat-minimize" data-ai-id="schnellchat-einklappen" aria-label="Schnellchat einklappen"
          title="Einklappen · Verlauf und Entwurf behalten" onClick={e => { e.stopPropagation(); einklappen(); }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
        </button>
      </header>
      <div className="schnellchat-context"><span className="schnellchat-context-dot" />Dein Hauptgespräch bleibt unberührt.
        {chat?.kontextAm && <span title="Gesprächskontext zuletzt gelesen">Stand {new Date(chat.kontextAm).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' })}</span>}
      </div>
      {modus === 'schnellbuehne' && html && <div className="schnellchat-preview"><iframe title="HTML-Darstellung" sandbox="" referrerPolicy="no-referrer" srcDoc={dokument} /></div>}
      <div className="schnellchat-messages" aria-live="polite">
        {chat?.nachrichten.map((n, i) => <article key={i} className={`schnellchat-message is-${n.role}`}>
          <div className="schnellchat-author">{n.role === 'user' ? 'Du' : '✦ Schnellberater'}</div><ReactMarkdown>{n.content}</ReactMarkdown>
          {n.html && <button type="button" className="schnellchat-show-html" onClick={() => { setDarstellung(i); waehleModus('schnellbuehne'); }}>Darstellung ansehen ↗</button>}</article>)}
        {chat?.laufend && <article className="schnellchat-message is-user"><ReactMarkdown>{chat.laufend.text}</ReactMarkdown></article>}
        {!chat?.nachrichten.length && !busy && <div className="schnellchat-empty">
          <div className="schnellchat-empty-mark" aria-hidden="true">✦</div>
          <h3>Direkt mit deinem Berater sprechen.</h3><p>Der Abzweig kennt das ganze Gespräch der Bühne. Dein Hauptgespräch bleibt unberührt, beim Beenden bekommt es eine Zusammenfassung.</p>
          <span>Zum Beispiel: „Was bedeutet das für mich?“</span>
        </div>}
        {busy && <div role="status" className="schnellchat-working"><span className="schnellchat-dot" />{chat ? 'Der Abzweig antwortet …' : 'Abzweig wird vorbereitet …'}</div>}
        <div ref={ende} />
      </div>
      {fehler && <div role="alert" className="schnellchat-error">{fehler} {!chat && <button onClick={() => void oeffnenNachFehler()}>Erneut laden</button>} {ohneOption && chat && <button type="button" onClick={() => void schliessen(true)}>Ohne Zusammenfassung beenden</button>}</div>}
      <form className="schnellchat-compose" onSubmit={e => { e.preventDefault(); void senden(); }}>
        <div className="schnellchat-modus" role="group" aria-label="Antwortmodus">
          <button type="button" aria-pressed={modus === 'frage'} onClick={() => waehleModus('frage')}>Chat</button>
          <button type="button" aria-pressed={modus === 'schnellbuehne'} onClick={() => waehleModus('schnellbuehne')}>Schnellbühne</button>
          <span>{modus === 'schnellbuehne' ? 'Änderungen direkt im HTML' : 'Antwort als Text'}</span>
        </div>
        <textarea ref={eingabe} aria-label="Deine Zwischenfrage" placeholder={modus === 'schnellbuehne' ? "Was soll ich darstellen oder ändern?" : "Was möchtest du wissen?"} value={text} maxLength={3000} rows={3}
          onChange={e => setText(e.target.value)} disabled={busy || !chat}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void senden(); } }} />
        <div className="schnellchat-compose-actions"><span>Enter zum Senden · ⇧ Enter für neue Zeile</span>
          <button type="submit" className="schnellchat-send" aria-label="Senden" disabled={busy || !chat || !text.trim()}>
            Senden
          </button>
        </div>
      </form>
      <footer className="schnellchat-footer"><span>Einklappen behält deinen Verlauf.</span>
        <button type="button" data-ai-id="schnellchat-schliessen" disabled={busy} onClick={() => void schliessen()}>Beenden & zusammenfassen <span aria-hidden="true">↗</span></button>
      </footer>
    </aside>;
  return <>
    <button ref={knopf} type="button" className={`schnellchat-trigger${offen ? ' is-open' : ''}`} data-ai-id="schnellchat-oeffnen"
      onClick={e => { e.stopPropagation(); void oeffnen(); }} aria-expanded={offen}
      title={offen ? 'Schnellchat einklappen – Gespräch behalten' : 'Zwischenfrage stellen oder Gespräch fortsetzen'}>
      <span aria-hidden="true">✦</span><span>Zwischenfrage</span>
      {busy ? <span className="schnellchat-dot" aria-label="Antwort läuft" /> : <span aria-hidden="true">{offen ? '⌄' : '›'}</span>}
    </button>
    {!offen && hinweis && <span role="status" className="schnellchat-receipt">{hinweis}</span>}
    {!offen && zusammenfassung && <details className="schnellchat-receipt"><summary>Zusammenfassung ansehen</summary><ReactMarkdown>{zusammenfassung}</ReactMarkdown></details>}
    {arbeitsbereich ? createPortal(fenster, arbeitsbereich) : fenster}
  </>;
}
